import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import api from "../apiClient";

// ===== Icons (inline SVG to avoid dependencies) =====
const Icons = {
  pending: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
  ),
  users: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  addUser: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/>
    </svg>
  ),
  grid: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
    </svg>
  ),
  check: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  x: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  shield: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  ),
  trash: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
    </svg>
  ),
  arrowLeft: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
    </svg>
  ),
  logout: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
  ),
  sparkle: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0L14.59 8.41L23 12L14.59 15.59L12 24L9.41 15.59L1 12L9.41 8.41L12 0Z"/>
    </svg>
  ),
  refresh: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
    </svg>
  ),
};

// ===== Tab config =====
const TABS = [
  { id: "pending", label: "Pending", icon: Icons.pending, color: "#f59e0b" },
  { id: "active", label: "Active Users", icon: Icons.users, color: "#3b82f6" },
  { id: "create", label: "Create User", icon: Icons.addUser, color: "#10b981" },
  { id: "app_access", label: "App Access", icon: Icons.grid, color: "#8b5cf6" },
];

export default function AdminDashboard({
  email,
  logout,
  onClose,
  pendingUsers,
  activeUsers,
  fetchActiveUsers,
  adminTab,
  setAdminTab,
  approveUser,
  rejectUser,
  deleteUser,
  toggleRole,
}) {
  // ===== Create User (local state) =====
  const [createEmail, setCreateEmail] = useState("");
  const [createPassword, setCreatePassword] = useState("");
  const [createIsAdmin, setCreateIsAdmin] = useState(false);
  const [createSuccess, setCreateSuccess] = useState(false);

  const createUser = async () => {
    try {
      await api.post("/admin/new_acct", {
        new_acct_email: createEmail,
        new_acct_pw: createPassword,
        is_admin: createIsAdmin,
      });
      setCreateEmail("");
      setCreatePassword("");
      setCreateIsAdmin(false);
      setCreateSuccess(true);
      setTimeout(() => setCreateSuccess(false), 2500);
      if (fetchActiveUsers) await fetchActiveUsers();
    } catch (err) {
      alert(err.response?.data?.detail || "User creation failed");
    }
  };

  // ===== App Access =====
  const [appsRegistry, setAppsRegistry] = useState([]);
  const [appsLoading, setAppsLoading] = useState(false);
  const [appsError, setAppsError] = useState("");
  const [selectedUserEmail, setSelectedUserEmail] = useState("");
  const [userAccessRows, setUserAccessRows] = useState([]);
  const [accessSaving, setAccessSaving] = useState(false);
  const [seedBusy, setSeedBusy] = useState(false);

  const activeByEmail = useMemo(() => {
    const out = Object.create(null);
    (activeUsers || []).forEach((u) => (out[u.email] = u));
    return out;
  }, [activeUsers]);

  const selectedUser = selectedUserEmail ? activeByEmail[selectedUserEmail] : null;

  const accessByKey = useMemo(() => {
    const m = Object.create(null);
    (userAccessRows || []).forEach((r) => (m[r.app_key] = r.allowed));
    return m;
  }, [userAccessRows]);

  async function loadRegistry() {
    setAppsLoading(true);
    setAppsError("");
    try {
      const res = await api.get("/apps/registry");
      setAppsRegistry(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setAppsError(err?.response?.data?.detail || "Failed to load app registry");
      setAppsRegistry([]);
    } finally {
      setAppsLoading(false);
    }
  }

  async function loadUserAccess(userEmail) {
    if (!userEmail) { setUserAccessRows([]); return; }
    try {
      const res = await api.get("/apps/access", { params: { email: userEmail } });
      setUserAccessRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.warn("get_user_access failed", err?.response?.data || err.message);
      setUserAccessRows([]);
    }
  }

  async function seedDefaults() {
    setSeedBusy(true);
    try {
      await api.post("/apps/seed_default");
      await loadRegistry();
    } catch (err) {
      alert(err?.response?.data?.detail || "Seeding failed");
    } finally {
      setSeedBusy(false);
    }
  }

  async function setAccess(userEmail, app_key, allowed) {
    setAccessSaving(true);
    try {
      await api.post("/apps/set_access", { email: userEmail, app_key, allowed });
      await loadUserAccess(userEmail);
    } catch (err) {
      alert(err?.response?.data?.detail || "Failed to update access");
    } finally {
      setAccessSaving(false);
    }
  }

  useEffect(() => {
    if (adminTab === "app_access") loadRegistry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminTab]);

  useEffect(() => {
    loadUserAccess(selectedUserEmail);
  }, [selectedUserEmail]);

  // ===== Animation: track previous tab for slide direction =====
  const [slideDir, setSlideDir] = useState("right");
  const prevTabRef = useRef(adminTab);

  const handleTabChange = useCallback((newTab) => {
    const oldIdx = TABS.findIndex((t) => t.id === prevTabRef.current);
    const newIdx = TABS.findIndex((t) => t.id === newTab);
    setSlideDir(newIdx >= oldIdx ? "right" : "left");
    prevTabRef.current = newTab;
    setAdminTab(newTab);
  }, [setAdminTab]);

  // ===== Floating orbs position =====
  const [mousePos, setMousePos] = useState({ x: 0.5, y: 0.5 });
  const containerRef = useRef(null);
  const handleMouseMove = useCallback((e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setMousePos({
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    });
  }, []);

  // Count stats
  const pendingCount = (pendingUsers || []).length;
  const activeCount = (activeUsers || []).length;
  const adminCount = (activeUsers || []).filter((u) => u.is_admin).length;

  const activeTab = TABS.find((t) => t.id === adminTab) || TABS[0];

  return (
    <div
      className="adm-root"
      ref={containerRef}
      onMouseMove={handleMouseMove}
    >
      <style>{STYLES}</style>

      {/* Floating ambient orbs that follow mouse subtly */}
      <div className="adm-orbs">
        <div
          className="adm-orb adm-orb-1"
          style={{
            transform: `translate(${mousePos.x * 40 - 20}px, ${mousePos.y * 40 - 20}px)`,
          }}
        />
        <div
          className="adm-orb adm-orb-2"
          style={{
            transform: `translate(${-mousePos.x * 30 + 15}px, ${-mousePos.y * 30 + 15}px)`,
          }}
        />
        <div
          className="adm-orb adm-orb-3"
          style={{
            transform: `translate(${mousePos.x * 20 - 10}px, ${mousePos.y * 25 - 12}px)`,
          }}
        />
      </div>

      {/* ===== Header ===== */}
      <header className="adm-header">
        <div className="adm-header-inner">
          <div className="adm-header-left">
            <div className="adm-header-title-group">
              <div className="adm-header-icon">{Icons.shield}</div>
              <div>
                <h1 className="adm-header-title">Admin Portal</h1>
                <p className="adm-header-sub">{email}</p>
              </div>
            </div>
          </div>

          {/* Live stats chips */}
          <div className="adm-stats-row">
            <StatChip label="Pending" value={pendingCount} color="#f59e0b" pulse={pendingCount > 0} />
            <StatChip label="Active" value={activeCount} color="#3b82f6" />
            <StatChip label="Admins" value={adminCount} color="#8b5cf6" />
          </div>

          <div className="adm-header-actions">
            <button onClick={onClose} className="adm-btn-ghost" title="Return to Apps">
              {Icons.arrowLeft}
              <span>Apps</span>
            </button>
            <button onClick={logout} className="adm-btn-ghost adm-btn-danger" title="Log out">
              {Icons.logout}
              <span>Logout</span>
            </button>
          </div>
        </div>
      </header>

      {/* ===== Navigation ===== */}
      <nav className="adm-nav">
        <div className="adm-nav-inner">
          {TABS.map((tab) => {
            const isActive = adminTab === tab.id;
            const count =
              tab.id === "pending" ? pendingCount :
              tab.id === "active" ? activeCount : null;
            return (
              <button
                key={tab.id}
                className={`adm-nav-btn ${isActive ? "adm-nav-btn-active" : ""}`}
                onClick={() => handleTabChange(tab.id)}
                style={isActive ? { "--tab-color": tab.color } : {}}
              >
                <span className="adm-nav-icon" style={isActive ? { color: tab.color } : {}}>
                  {tab.icon}
                </span>
                <span className="adm-nav-label">{tab.label}</span>
                {count !== null && count > 0 && (
                  <span
                    className="adm-nav-badge"
                    style={{ background: tab.color }}
                  >
                    {count}
                  </span>
                )}
                {isActive && <span className="adm-nav-indicator" style={{ background: tab.color }} />}
              </button>
            );
          })}
        </div>
      </nav>

      {/* ===== Content ===== */}
      <main className="adm-main">
        <div
          key={adminTab}
          className={`adm-panel-enter adm-panel-enter-${slideDir}`}
        >
          {adminTab === "pending" && (
            <PendingPanel
              pendingUsers={pendingUsers}
              approveUser={approveUser}
              rejectUser={rejectUser}
            />
          )}
          {adminTab === "active" && (
            <ActivePanel
              activeUsers={activeUsers}
              toggleRole={toggleRole}
              deleteUser={deleteUser}
            />
          )}
          {adminTab === "create" && (
            <CreatePanel
              createEmail={createEmail}
              setCreateEmail={setCreateEmail}
              createPassword={createPassword}
              setCreatePassword={setCreatePassword}
              createIsAdmin={createIsAdmin}
              setCreateIsAdmin={setCreateIsAdmin}
              createUser={createUser}
              createSuccess={createSuccess}
            />
          )}
          {adminTab === "app_access" && (
            <AppAccessPanel
              appsRegistry={appsRegistry}
              appsLoading={appsLoading}
              appsError={appsError}
              activeUsers={activeUsers}
              selectedUserEmail={selectedUserEmail}
              setSelectedUserEmail={setSelectedUserEmail}
              selectedUser={selectedUser}
              accessByKey={accessByKey}
              accessSaving={accessSaving}
              setAccess={setAccess}
              seedDefaults={seedDefaults}
              seedBusy={seedBusy}
              loadRegistry={loadRegistry}
            />
          )}
        </div>
      </main>
    </div>
  );
}

// =====================================================================
// Module-scope sub-components (prevents re-mount on parent re-render)
// =====================================================================

function StatChip({ label, value, color, pulse }) {
  return (
    <div className="adm-stat-chip" style={{ "--chip-color": color }}>
      {pulse && <span className="adm-stat-pulse" />}
      <span className="adm-stat-value">{value}</span>
      <span className="adm-stat-label">{label}</span>
    </div>
  );
}

function Avatar({ email, isAdmin, size = 36 }) {
  const initials = email
    ? email.split("@")[0].slice(0, 2).toUpperCase()
    : "??";
  const hue = email
    ? [...email].reduce((a, c) => a + c.charCodeAt(0), 0) % 360
    : 200;
  return (
    <div
      className="adm-avatar"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.38,
        background: `hsl(${hue}, 55%, 92%)`,
        color: `hsl(${hue}, 55%, 35%)`,
        border: isAdmin ? "2px solid #f59e0b" : "2px solid transparent",
      }}
      title={email}
    >
      {initials}
      {isAdmin && (
        <span className="adm-avatar-crown" title="Admin">
          {Icons.shield}
        </span>
      )}
    </div>
  );
}

// ===== Pending Panel =====
function PendingPanel({ pendingUsers, approveUser, rejectUser }) {
  const [animatingOut, setAnimatingOut] = useState({});

  const handleAction = async (userEmail, action) => {
    setAnimatingOut((prev) => ({ ...prev, [userEmail]: action }));
    // small delay for animation then call the real action
    await new Promise((r) => setTimeout(r, 400));
    if (action === "approve") await approveUser(userEmail);
    else await rejectUser(userEmail);
  };

  return (
    <div className="adm-section">
      <SectionHeader
        icon={Icons.pending}
        title="Pending Approvals"
        subtitle={`${(pendingUsers || []).length} user${(pendingUsers || []).length !== 1 ? "s" : ""} awaiting review`}
        color="#f59e0b"
      />
      {(!pendingUsers || pendingUsers.length === 0) ? (
        <EmptyState
          icon={Icons.check}
          title="All clear!"
          subtitle="No pending users to review."
        />
      ) : (
        <div className="adm-card-grid">
          {pendingUsers.map((u, i) => {
            const outClass = animatingOut[u.email]
              ? `adm-card-out-${animatingOut[u.email]}`
              : "";
            return (
              <div
                key={u.email}
                className={`adm-user-card adm-stagger ${outClass}`}
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div className="adm-user-card-top">
                  <Avatar email={u.email} size={44} />
                  <div className="adm-user-info">
                    <div className="adm-user-email">{u.email}</div>
                    <Badge tone="amber">Pending Approval</Badge>
                  </div>
                </div>
                <div className="adm-user-card-actions">
                  <button
                    onClick={() => handleAction(u.email, "approve")}
                    className="adm-action-btn adm-action-approve"
                    title="Approve"
                  >
                    {Icons.check}
                    <span>Approve</span>
                  </button>
                  <button
                    onClick={() => handleAction(u.email, "reject")}
                    className="adm-action-btn adm-action-reject"
                    title="Reject"
                  >
                    {Icons.x}
                    <span>Reject</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ===== Active Panel =====
function ActivePanel({ activeUsers, toggleRole, deleteUser }) {
  const [confirmDelete, setConfirmDelete] = useState(null);

  return (
    <div className="adm-section">
      <SectionHeader
        icon={Icons.users}
        title="Active Users"
        subtitle={`${(activeUsers || []).length} user${(activeUsers || []).length !== 1 ? "s" : ""} with access`}
        color="#3b82f6"
      />
      {(!activeUsers || activeUsers.length === 0) ? (
        <EmptyState
          icon={Icons.users}
          title="No users yet"
          subtitle="Create or approve users to see them here."
        />
      ) : (
        <div className="adm-card-grid">
          {activeUsers.map((u, i) => (
            <div
              key={u.email}
              className="adm-user-card adm-stagger"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <div className="adm-user-card-top">
                <Avatar email={u.email} isAdmin={u.is_admin} size={44} />
                <div className="adm-user-info">
                  <div className="adm-user-email">{u.email}</div>
                  {u.is_admin ? (
                    <Badge tone="amber">Admin</Badge>
                  ) : (
                    <Badge tone="blue">User</Badge>
                  )}
                </div>
              </div>
              <div className="adm-user-card-actions">
                <button
                  onClick={() => toggleRole(u.email, u.is_admin)}
                  className="adm-action-btn adm-action-role"
                  title={u.is_admin ? "Demote to User" : "Promote to Admin"}
                >
                  {Icons.shield}
                  <span>{u.is_admin ? "Demote" : "Promote"}</span>
                </button>
                {confirmDelete === u.email ? (
                  <div className="adm-confirm-group">
                    <button
                      onClick={() => { deleteUser(u.email); setConfirmDelete(null); }}
                      className="adm-action-btn adm-action-reject"
                      title="Confirm delete"
                    >
                      Yes, delete
                    </button>
                    <button
                      onClick={() => setConfirmDelete(null)}
                      className="adm-action-btn"
                      title="Cancel"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmDelete(u.email)}
                    className="adm-action-btn adm-action-delete"
                    title="Delete user"
                  >
                    {Icons.trash}
                    <span>Delete</span>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ===== Create Panel =====
function CreatePanel({
  createEmail, setCreateEmail,
  createPassword, setCreatePassword,
  createIsAdmin, setCreateIsAdmin,
  createUser, createSuccess,
}) {
  const [step, setStep] = useState(0); // 0=email, 1=password, 2=role, 3=confirm
  const emailRef = useRef(null);
  const passRef = useRef(null);

  useEffect(() => {
    if (step === 0 && emailRef.current) emailRef.current.focus();
    if (step === 1 && passRef.current) passRef.current.focus();
  }, [step]);

  const canProceedEmail = createEmail.includes("@") && createEmail.includes(".");
  const canProceedPass = createPassword.length >= 4;

  const handleCreate = async () => {
    await createUser();
    setStep(0);
  };

  if (createSuccess) {
    return (
      <div className="adm-section">
        <div className="adm-success-splash">
          <div className="adm-success-ring" />
          <div className="adm-success-check">{Icons.check}</div>
          <h3 className="adm-success-title">User Created!</h3>
          <p className="adm-success-sub">The new account is ready to use.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="adm-section">
      <SectionHeader
        icon={Icons.addUser}
        title="Create User"
        subtitle="Set up a new account in a few steps"
        color="#10b981"
      />

      {/* Progress steps */}
      <div className="adm-wizard-progress">
        {["Email", "Password", "Role", "Confirm"].map((label, i) => (
          <div
            key={label}
            className={`adm-wizard-step ${i <= step ? "adm-wizard-step-active" : ""} ${i < step ? "adm-wizard-step-done" : ""}`}
            onClick={() => { if (i < step) setStep(i); }}
          >
            <div className="adm-wizard-dot">
              {i < step ? Icons.check : <span>{i + 1}</span>}
            </div>
            <span className="adm-wizard-label">{label}</span>
            {i < 3 && <div className={`adm-wizard-line ${i < step ? "adm-wizard-line-done" : ""}`} />}
          </div>
        ))}
      </div>

      {/* Step panels */}
      <div className="adm-wizard-panel" key={step}>
        {step === 0 && (
          <div className="adm-wizard-content adm-stagger">
            <label className="adm-field-label">Email Address</label>
            <input
              ref={emailRef}
              type="email"
              className="adm-field-input"
              placeholder="user@example.com"
              value={createEmail}
              onChange={(e) => setCreateEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && canProceedEmail) setStep(1); }}
            />
            <button
              className="adm-wizard-next"
              disabled={!canProceedEmail}
              onClick={() => setStep(1)}
            >
              Continue
            </button>
          </div>
        )}
        {step === 1 && (
          <div className="adm-wizard-content adm-stagger">
            <label className="adm-field-label">Password</label>
            <input
              ref={passRef}
              type="password"
              className="adm-field-input"
              placeholder="Min. 4 characters"
              value={createPassword}
              onChange={(e) => setCreatePassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && canProceedPass) setStep(2); }}
            />
            <div className="adm-wizard-btn-row">
              <button className="adm-wizard-back" onClick={() => setStep(0)}>Back</button>
              <button
                className="adm-wizard-next"
                disabled={!canProceedPass}
                onClick={() => setStep(2)}
              >
                Continue
              </button>
            </div>
          </div>
        )}
        {step === 2 && (
          <div className="adm-wizard-content adm-stagger">
            <label className="adm-field-label">Role</label>
            <div className="adm-role-picker">
              <button
                className={`adm-role-option ${!createIsAdmin ? "adm-role-option-active" : ""}`}
                onClick={() => setCreateIsAdmin(false)}
              >
                <div className="adm-role-option-icon" style={{ color: "#3b82f6" }}>{Icons.users}</div>
                <div className="adm-role-option-label">Standard User</div>
                <div className="adm-role-option-desc">Regular access, no admin privileges</div>
              </button>
              <button
                className={`adm-role-option ${createIsAdmin ? "adm-role-option-active" : ""}`}
                onClick={() => setCreateIsAdmin(true)}
              >
                <div className="adm-role-option-icon" style={{ color: "#f59e0b" }}>{Icons.shield}</div>
                <div className="adm-role-option-label">Admin</div>
                <div className="adm-role-option-desc">Full control over users & settings</div>
              </button>
            </div>
            <div className="adm-wizard-btn-row">
              <button className="adm-wizard-back" onClick={() => setStep(1)}>Back</button>
              <button className="adm-wizard-next" onClick={() => setStep(3)}>
                Review
              </button>
            </div>
          </div>
        )}
        {step === 3 && (
          <div className="adm-wizard-content adm-stagger">
            <label className="adm-field-label">Review & Create</label>
            <div className="adm-review-card">
              <Avatar email={createEmail} isAdmin={createIsAdmin} size={56} />
              <div className="adm-review-details">
                <div className="adm-review-email">{createEmail}</div>
                <div className="adm-review-role">
                  {createIsAdmin ? (
                    <Badge tone="amber">Admin</Badge>
                  ) : (
                    <Badge tone="blue">Standard User</Badge>
                  )}
                </div>
              </div>
            </div>
            <div className="adm-wizard-btn-row">
              <button className="adm-wizard-back" onClick={() => setStep(2)}>Back</button>
              <button className="adm-wizard-create" onClick={handleCreate}>
                {Icons.sparkle}
                <span>Create User</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ===== App Access Panel =====
function AppAccessPanel({
  appsRegistry, appsLoading, appsError,
  activeUsers, selectedUserEmail, setSelectedUserEmail,
  selectedUser, accessByKey, accessSaving,
  setAccess, seedDefaults, seedBusy, loadRegistry,
}) {
  return (
    <div className="adm-section">
      <div className="adm-section-header-row">
        <SectionHeader
          icon={Icons.grid}
          title="App Access"
          subtitle="Control which apps each user can see"
          color="#8b5cf6"
        />
        <div className="adm-section-actions">
          <button
            onClick={seedDefaults}
            disabled={seedBusy}
            className="adm-btn-subtle"
            title="Create default app records"
          >
            {Icons.sparkle}
            <span>{seedBusy ? "Seeding..." : "Seed Defaults"}</span>
          </button>
          <button
            onClick={loadRegistry}
            className="adm-btn-subtle"
            title="Reload app list"
          >
            {Icons.refresh}
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* User picker as horizontal scroll of avatars */}
      <div className="adm-user-picker">
        <span className="adm-picker-label">Select user:</span>
        <div className="adm-picker-scroll">
          {(activeUsers || []).map((u) => {
            const selected = selectedUserEmail === u.email;
            return (
              <button
                key={u.email}
                className={`adm-picker-item ${selected ? "adm-picker-item-active" : ""}`}
                onClick={() => setSelectedUserEmail(u.email)}
                title={u.email}
              >
                <Avatar email={u.email} isAdmin={u.is_admin} size={38} />
                <span className="adm-picker-email">
                  {u.email.split("@")[0]}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Apps grid */}
      <div className="adm-access-body">
        {appsLoading ? (
          <div className="adm-loading">
            <div className="adm-spinner" />
            <span>Loading apps...</span>
          </div>
        ) : appsError ? (
          <div className="adm-error-msg">{appsError}</div>
        ) : (appsRegistry || []).length === 0 ? (
          <EmptyState
            icon={Icons.grid}
            title="No apps registered"
            subtitle="Click Seed Defaults to create initial app records."
          />
        ) : !selectedUser ? (
          <EmptyState
            icon={Icons.users}
            title="Pick a user"
            subtitle="Select a user above to manage their app access."
          />
        ) : (
          <>
            {selectedUser && (
              <div className="adm-selected-banner">
                Managing access for <strong>{selectedUser.email}</strong>
                {selectedUser.is_admin && (
                  <Badge tone="amber">Admin</Badge>
                )}
              </div>
            )}
            <div className="adm-app-grid">
              {appsRegistry.map((app, i) => {
                const allowed = !!accessByKey[app.key];
                const disabled = accessSaving || (app.is_admin_only && !selectedUser.is_admin);
                return (
                  <div
                    key={app.key}
                    className={`adm-app-card adm-stagger ${allowed ? "adm-app-card-allowed" : ""}`}
                    style={{ animationDelay: `${i * 80}ms` }}
                  >
                    <div className="adm-app-card-info">
                      <div className="adm-app-name">{app.title || app.key.toUpperCase()}</div>
                      <div className="adm-app-desc">
                        {app.description || (app.is_public ? "Public app" : "Restricted app")}
                      </div>
                      <div className="adm-app-badges">
                        {app.is_public && <Badge tone="green">Public</Badge>}
                        {app.is_admin_only && <Badge tone="amber">Admin-only</Badge>}
                      </div>
                    </div>
                    <button
                      onClick={() => setAccess(selectedUser.email, app.key, !allowed)}
                      disabled={disabled}
                      className={`adm-toggle ${allowed ? "adm-toggle-on" : ""} ${disabled ? "adm-toggle-disabled" : ""}`}
                      title={allowed ? "Revoke access" : "Grant access"}
                    >
                      <span className="adm-toggle-thumb" />
                      <span className="adm-toggle-label">{allowed ? "ON" : "OFF"}</span>
                    </button>
                  </div>
                );
              })}
            </div>
            {selectedUser && !selectedUser.is_admin && (
              <div className="adm-access-note">
                Admin-only apps are locked for non-admin users, even if toggled on here.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function SectionHeader({ icon, title, subtitle, color }) {
  return (
    <div className="adm-section-header">
      <div className="adm-section-icon" style={{ color, background: `${color}15` }}>{icon}</div>
      <div>
        <h2 className="adm-section-title">{title}</h2>
        {subtitle && <p className="adm-section-sub">{subtitle}</p>}
      </div>
    </div>
  );
}

function EmptyState({ icon, title, subtitle }) {
  return (
    <div className="adm-empty">
      <div className="adm-empty-icon">{icon}</div>
      <div className="adm-empty-title">{title}</div>
      {subtitle && <div className="adm-empty-sub">{subtitle}</div>}
    </div>
  );
}

function Badge({ tone = "blue", children }) {
  const tones = {
    blue:  { bg: "#eff6ff", border: "#bfdbfe", color: "#1d4ed8" },
    amber: { bg: "#fffbeb", border: "#fde68a", color: "#b45309" },
    green: { bg: "#ecfdf5", border: "#a7f3d0", color: "#047857" },
    red:   { bg: "#fef2f2", border: "#fecaca", color: "#b91c1c" },
    gray:  { bg: "#f9fafb", border: "#e5e7eb", color: "#4b5563" },
  };
  const t = tones[tone] || tones.gray;
  return (
    <span
      className="adm-badge"
      style={{ background: t.bg, borderColor: t.border, color: t.color }}
    >
      {children}
    </span>
  );
}

// =====================================================================
// Styles
// =====================================================================
const STYLES = `
/* ===== Root ===== */
.adm-root {
  min-height: 100vh;
  background: #f8fafc;
  position: relative;
  overflow-x: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* ===== Floating Orbs ===== */
.adm-orbs {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
}
.adm-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.35;
  transition: transform 0.8s cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform;
}
.adm-orb-1 {
  width: 400px; height: 400px;
  background: radial-gradient(circle, #93c5fd 0%, transparent 70%);
  top: -100px; right: -80px;
  animation: adm-float 12s ease-in-out infinite;
}
.adm-orb-2 {
  width: 350px; height: 350px;
  background: radial-gradient(circle, #c4b5fd 0%, transparent 70%);
  bottom: -50px; left: -100px;
  animation: adm-float 15s ease-in-out infinite reverse;
}
.adm-orb-3 {
  width: 250px; height: 250px;
  background: radial-gradient(circle, #fde68a 0%, transparent 70%);
  top: 50%; left: 50%;
  animation: adm-float 18s ease-in-out infinite 3s;
  opacity: 0.2;
}
@keyframes adm-float {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(30px, -20px) scale(1.05); }
  66% { transform: translate(-20px, 15px) scale(0.95); }
}

/* ===== Header ===== */
.adm-header {
  position: sticky;
  top: 0;
  z-index: 20;
  background: rgba(255,255,255,0.82);
  backdrop-filter: blur(20px) saturate(1.8);
  -webkit-backdrop-filter: blur(20px) saturate(1.8);
  border-bottom: 1px solid rgba(0,0,0,0.06);
}
.adm-header-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 12px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.adm-header-left { display: flex; align-items: center; gap: 12px; }
.adm-header-title-group { display: flex; align-items: center; gap: 10px; }
.adm-header-icon {
  width: 36px; height: 36px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 10px;
  background: linear-gradient(135deg, #eff6ff, #dbeafe);
  color: #3b82f6;
}
.adm-header-title {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.3px;
  color: #0f172a;
  margin: 0;
}
.adm-header-sub {
  font-size: 12px;
  color: #94a3b8;
  margin: 0;
}

/* Stats chips */
.adm-stats-row {
  display: flex;
  gap: 8px;
  align-items: center;
}
.adm-stat-chip {
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  background: white;
  border: 1px solid #e2e8f0;
  font-size: 12px;
  transition: all 0.25s ease;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}
.adm-stat-chip:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  transform: translateY(-1px);
}
.adm-stat-value {
  font-weight: 700;
  font-size: 14px;
  color: var(--chip-color);
}
.adm-stat-label { color: #64748b; }
.adm-stat-pulse {
  position: absolute;
  top: 4px; right: 4px;
  width: 8px; height: 8px;
  border-radius: 50%;
  background: #f59e0b;
  animation: adm-pulse 2s ease-in-out infinite;
}
@keyframes adm-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.4; transform: scale(1.5); }
}

/* Header buttons */
.adm-header-actions { display: flex; gap: 8px; }
.adm-btn-ghost {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: white;
  color: #475569;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}
.adm-btn-ghost:hover {
  border-color: #cbd5e1;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  transform: translateY(-1px);
}
.adm-btn-danger { color: #ef4444; border-color: #fecaca; }
.adm-btn-danger:hover { border-color: #f87171; background: #fef2f2; }

/* ===== Navigation ===== */
.adm-nav {
  position: sticky;
  top: 65px;
  z-index: 15;
  background: rgba(255,255,255,0.7);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-bottom: 1px solid rgba(0,0,0,0.04);
}
.adm-nav-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 8px 24px;
  display: flex;
  gap: 4px;
}
.adm-nav-btn {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 12px;
  border: none;
  background: transparent;
  color: #64748b;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.22, 1, 0.36, 1);
  overflow: hidden;
}
.adm-nav-btn:hover {
  background: #f1f5f9;
  color: #334155;
}
.adm-nav-btn-active {
  background: white;
  color: #0f172a;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04);
}
.adm-nav-icon {
  display: flex;
  align-items: center;
  transition: color 0.25s ease;
}
.adm-nav-label { white-space: nowrap; }
.adm-nav-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  color: white;
  font-size: 11px;
  font-weight: 700;
  animation: adm-badge-pop 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}
@keyframes adm-badge-pop {
  0% { transform: scale(0); }
  100% { transform: scale(1); }
}
.adm-nav-indicator {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 24px;
  height: 3px;
  border-radius: 3px 3px 0 0;
  animation: adm-indicator-in 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}
@keyframes adm-indicator-in {
  0% { width: 0; opacity: 0; }
  100% { width: 24px; opacity: 1; }
}

/* ===== Main content ===== */
.adm-main {
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 24px 64px;
}

/* Panel enter animations */
.adm-panel-enter {
  animation: adm-panel-slide 0.4s cubic-bezier(0.22, 1, 0.36, 1) both;
}
.adm-panel-enter-right {
  animation-name: adm-slide-right;
}
.adm-panel-enter-left {
  animation-name: adm-slide-left;
}
@keyframes adm-slide-right {
  0% { opacity: 0; transform: translateX(30px); }
  100% { opacity: 1; transform: translateX(0); }
}
@keyframes adm-slide-left {
  0% { opacity: 0; transform: translateX(-30px); }
  100% { opacity: 1; transform: translateX(0); }
}

/* Stagger animation for cards */
.adm-stagger {
  animation: adm-stagger-in 0.45s cubic-bezier(0.22, 1, 0.36, 1) both;
}
@keyframes adm-stagger-in {
  0% { opacity: 0; transform: translateY(16px) scale(0.97); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}

/* ===== Section ===== */
.adm-section {}
.adm-section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
}
.adm-section-header-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}
.adm-section-header-row .adm-section-header { margin-bottom: 0; }
.adm-section-icon {
  width: 40px; height: 40px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 12px;
  flex-shrink: 0;
}
.adm-section-title {
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  letter-spacing: -0.3px;
}
.adm-section-sub {
  font-size: 13px;
  color: #94a3b8;
  margin: 2px 0 0 0;
}
.adm-section-actions {
  display: flex;
  gap: 8px;
}
.adm-btn-subtle {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: white;
  color: #475569;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}
.adm-btn-subtle:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}
.adm-btn-subtle:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

/* ===== Card Grid ===== */
.adm-card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
}

/* ===== User Card ===== */
.adm-user-card {
  background: white;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  padding: 20px;
  transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
  position: relative;
  overflow: hidden;
}
.adm-user-card::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 16px;
  background: linear-gradient(135deg, transparent 60%, rgba(59,130,246,0.03));
  pointer-events: none;
}
.adm-user-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 8px 24px rgba(0,0,0,0.06), 0 0 0 1px rgba(59,130,246,0.05);
  transform: translateY(-2px);
}
.adm-user-card-top {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;
}
.adm-user-info { flex: 1; min-width: 0; }
.adm-user-email {
  font-weight: 600;
  font-size: 14px;
  color: #0f172a;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.adm-user-card-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.adm-confirm-group {
  display: flex;
  gap: 6px;
  animation: adm-stagger-in 0.25s ease both;
}

/* Card out animations */
.adm-card-out-approve {
  animation: adm-card-approve 0.5s ease forwards;
}
.adm-card-out-reject {
  animation: adm-card-reject 0.5s ease forwards;
}
@keyframes adm-card-approve {
  0% { opacity: 1; transform: translateX(0) scale(1); }
  50% { opacity: 0.6; transform: translateX(20px) scale(1.02); border-color: #10b981; }
  100% { opacity: 0; transform: translateX(60px) scale(0.95); height: 0; padding: 0; margin: 0; }
}
@keyframes adm-card-reject {
  0% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(0.98); border-color: #ef4444; }
  100% { opacity: 0; transform: scale(0.9); height: 0; padding: 0; margin: 0; }
}

/* ===== Action Buttons ===== */
.adm-action-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: white;
  color: #475569;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}
.adm-action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 10px rgba(0,0,0,0.08);
}
.adm-action-approve {
  color: #059669;
  border-color: #a7f3d0;
}
.adm-action-approve:hover {
  background: #ecfdf5;
  border-color: #6ee7b7;
}
.adm-action-reject {
  color: #dc2626;
  border-color: #fecaca;
}
.adm-action-reject:hover {
  background: #fef2f2;
  border-color: #f87171;
}
.adm-action-role {
  color: #7c3aed;
  border-color: #c4b5fd;
}
.adm-action-role:hover {
  background: #f5f3ff;
  border-color: #a78bfa;
}
.adm-action-delete {
  color: #dc2626;
  border-color: #fecaca;
}
.adm-action-delete:hover {
  background: #fef2f2;
  border-color: #f87171;
}

/* ===== Avatar ===== */
.adm-avatar {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-weight: 700;
  letter-spacing: 0.5px;
  flex-shrink: 0;
  transition: transform 0.2s ease;
}
.adm-avatar:hover { transform: scale(1.08); }
.adm-avatar-crown {
  position: absolute;
  top: -4px;
  right: -4px;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fffbeb;
  border-radius: 50%;
  color: #f59e0b;
  border: 2px solid white;
}

/* ===== Badge ===== */
.adm-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  border: 1px solid;
  letter-spacing: 0.2px;
  white-space: nowrap;
}

/* ===== Empty State ===== */
.adm-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  animation: adm-stagger-in 0.5s ease both;
}
.adm-empty-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  background: #f1f5f9;
  color: #94a3b8;
  margin-bottom: 16px;
}
.adm-empty-title {
  font-size: 16px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 4px;
}
.adm-empty-sub {
  font-size: 13px;
  color: #94a3b8;
}

/* ===== Wizard (Create User) ===== */
.adm-wizard-progress {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  margin-bottom: 32px;
  padding: 0 16px;
}
.adm-wizard-step {
  display: flex;
  align-items: center;
  gap: 0;
  cursor: default;
}
.adm-wizard-step-done { cursor: pointer; }
.adm-wizard-dot {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #f1f5f9;
  color: #94a3b8;
  font-size: 13px;
  font-weight: 600;
  border: 2px solid #e2e8f0;
  transition: all 0.3s ease;
  flex-shrink: 0;
}
.adm-wizard-step-active .adm-wizard-dot {
  background: #eff6ff;
  border-color: #3b82f6;
  color: #1d4ed8;
}
.adm-wizard-step-done .adm-wizard-dot {
  background: #10b981;
  border-color: #10b981;
  color: white;
}
.adm-wizard-label {
  font-size: 12px;
  color: #94a3b8;
  margin-left: 6px;
  font-weight: 500;
  white-space: nowrap;
}
.adm-wizard-step-active .adm-wizard-label { color: #1d4ed8; }
.adm-wizard-step-done .adm-wizard-label { color: #059669; }
.adm-wizard-line {
  width: 40px;
  height: 2px;
  background: #e2e8f0;
  margin: 0 8px;
  border-radius: 2px;
  transition: background 0.3s ease;
}
.adm-wizard-line-done { background: #10b981; }

.adm-wizard-panel {
  max-width: 480px;
  margin: 0 auto;
}
.adm-wizard-content {
  background: white;
  border-radius: 20px;
  border: 1px solid #e2e8f0;
  padding: 32px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.04);
}

.adm-field-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 10px;
}
.adm-field-input {
  width: 100%;
  padding: 12px 16px;
  border-radius: 12px;
  border: 2px solid #e2e8f0;
  font-size: 15px;
  color: #0f172a;
  outline: none;
  transition: all 0.2s ease;
  background: #f8fafc;
  margin-bottom: 20px;
  box-sizing: border-box;
}
.adm-field-input:focus {
  border-color: #93c5fd;
  background: white;
  box-shadow: 0 0 0 4px rgba(59,130,246,0.1);
}
.adm-field-input::placeholder { color: #cbd5e1; }

.adm-wizard-btn-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}
.adm-wizard-next {
  flex: 1;
  padding: 12px 24px;
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}
.adm-wizard-next:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 16px rgba(37,99,235,0.3);
}
.adm-wizard-next:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.adm-wizard-back {
  padding: 12px 20px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: white;
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}
.adm-wizard-back:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}
.adm-wizard-create {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}
.adm-wizard-create:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 16px rgba(5,150,105,0.3);
}

/* Role picker */
.adm-role-picker {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 24px;
}
.adm-role-option {
  padding: 20px;
  border-radius: 16px;
  border: 2px solid #e2e8f0;
  background: white;
  cursor: pointer;
  text-align: center;
  transition: all 0.25s cubic-bezier(0.22, 1, 0.36, 1);
}
.adm-role-option:hover {
  border-color: #cbd5e1;
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0,0,0,0.06);
}
.adm-role-option-active {
  border-color: #3b82f6;
  background: #eff6ff;
  box-shadow: 0 0 0 4px rgba(59,130,246,0.1);
}
.adm-role-option-icon {
  display: flex;
  justify-content: center;
  margin-bottom: 8px;
}
.adm-role-option-label {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 4px;
}
.adm-role-option-desc {
  font-size: 11px;
  color: #94a3b8;
}

/* Review card */
.adm-review-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  border-radius: 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  margin-bottom: 24px;
}
.adm-review-details { flex: 1; }
.adm-review-email {
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 6px;
}

/* Success splash */
.adm-success-splash {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  text-align: center;
  position: relative;
}
.adm-success-ring {
  position: absolute;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  border: 3px solid #10b981;
  animation: adm-success-ring-anim 0.8s ease-out;
  opacity: 0.3;
}
@keyframes adm-success-ring-anim {
  0% { transform: scale(0.5); opacity: 0; }
  50% { opacity: 0.5; }
  100% { transform: scale(2.5); opacity: 0; }
}
.adm-success-check {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  margin-bottom: 20px;
  animation: adm-success-pop 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) both;
}
@keyframes adm-success-pop {
  0% { transform: scale(0); }
  100% { transform: scale(1); }
}
.adm-success-title {
  font-size: 22px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 6px;
}
.adm-success-sub {
  font-size: 14px;
  color: #94a3b8;
  margin: 0;
}

/* ===== App Access Panel ===== */
.adm-user-picker {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f1f5f9;
}
.adm-picker-label {
  font-size: 12px;
  font-weight: 600;
  color: #94a3b8;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.adm-picker-scroll {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 4px 0;
  scrollbar-width: thin;
  scrollbar-color: #e2e8f0 transparent;
}
.adm-picker-scroll::-webkit-scrollbar { height: 4px; }
.adm-picker-scroll::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 4px; }
.adm-picker-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  border-radius: 14px;
  border: 2px solid transparent;
  background: transparent;
  cursor: pointer;
  transition: all 0.25s ease;
  min-width: 64px;
}
.adm-picker-item:hover {
  background: #f1f5f9;
}
.adm-picker-item-active {
  background: #eff6ff;
  border-color: #93c5fd;
  box-shadow: 0 0 0 3px rgba(59,130,246,0.08);
}
.adm-picker-email {
  font-size: 10px;
  color: #64748b;
  max-width: 72px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.adm-picker-item-active .adm-picker-email { color: #1d4ed8; font-weight: 600; }

.adm-selected-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  font-size: 13px;
  color: #475569;
  margin-bottom: 20px;
}

.adm-app-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}
.adm-app-card {
  background: white;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  transition: all 0.3s ease;
}
.adm-app-card:hover {
  border-color: #cbd5e1;
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0,0,0,0.05);
}
.adm-app-card-allowed {
  border-color: #a7f3d0;
  background: linear-gradient(135deg, white, #ecfdf5);
}
.adm-app-card-info { flex: 1; min-width: 0; }
.adm-app-name {
  font-weight: 600;
  font-size: 14px;
  color: #0f172a;
  margin-bottom: 4px;
}
.adm-app-desc {
  font-size: 12px;
  color: #94a3b8;
  margin-bottom: 8px;
}
.adm-app-badges {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

/* Toggle switch */
.adm-toggle {
  position: relative;
  width: 52px;
  height: 28px;
  border-radius: 14px;
  border: none;
  background: #e2e8f0;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
  flex-shrink: 0;
  padding: 0;
}
.adm-toggle:hover:not(.adm-toggle-disabled) {
  box-shadow: 0 0 0 4px rgba(0,0,0,0.04);
}
.adm-toggle-on {
  background: #10b981;
}
.adm-toggle-on:hover:not(.adm-toggle-disabled) {
  box-shadow: 0 0 0 4px rgba(16,185,129,0.15);
}
.adm-toggle-thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: white;
  box-shadow: 0 1px 4px rgba(0,0,0,0.15);
  transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}
.adm-toggle-on .adm-toggle-thumb {
  left: 27px;
}
.adm-toggle-label {
  position: absolute;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.5px;
  top: 50%;
  transform: translateY(-50%);
  color: white;
  opacity: 0;
  transition: opacity 0.2s;
}
.adm-toggle-on .adm-toggle-label {
  left: 8px;
  opacity: 0.9;
}
.adm-toggle-disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.adm-access-note {
  margin-top: 16px;
  padding: 10px 14px;
  border-radius: 10px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  font-size: 12px;
  color: #92400e;
}

/* ===== Loading ===== */
.adm-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 48px 24px;
  font-size: 13px;
  color: #94a3b8;
}
.adm-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #e2e8f0;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: adm-spin 0.6s linear infinite;
}
@keyframes adm-spin {
  to { transform: rotate(360deg); }
}
.adm-error-msg {
  padding: 16px;
  border-radius: 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  font-size: 13px;
}

/* ===== Responsive ===== */
@media (max-width: 768px) {
  .adm-header-inner {
    flex-direction: column;
    align-items: flex-start;
  }
  .adm-stats-row { order: 3; }
  .adm-nav-inner {
    overflow-x: auto;
    padding-bottom: 8px;
  }
  .adm-card-grid {
    grid-template-columns: 1fr;
  }
  .adm-app-grid {
    grid-template-columns: 1fr;
  }
  .adm-role-picker {
    grid-template-columns: 1fr;
  }
  .adm-wizard-progress {
    flex-wrap: wrap;
    gap: 4px;
  }
  .adm-wizard-line { width: 24px; }
  .adm-user-picker {
    flex-direction: column;
    align-items: flex-start;
  }
}
`;
